// src/pages/ValidacionDocumentos.js
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import {
    FaCloudUploadAlt,
    FaDownload,
    FaEye,
    FaFileAlt,
    FaFilter,
    FaFolderOpen,
    FaHammer,
    FaPlus,
    FaTrashAlt,
    FaUserCircle,
    FaCalendarAlt,
    FaClock,
} from 'react-icons/fa';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import './ValidacionDocumentos.css';
import './ValidacionDocumentosMobile.css';
import SolicitudMudanzaModal from '../components/SolicitudMudanzaModal';
import SolicitudConstruccionModal from '../components/SolicitudConstruccionModal';
import SolicitudRemodelacionModal from '../components/SolicitudRemodelacionModal';
import ConfirmDialog from '../components/ConfirmDialog';
import SolicitudMudanzaReviewModal from '../components/SolicitudMudanzaReviewModal';
import ReglamentoUploadModal from '../components/ReglamentoUploadModal';
import PermisosDocumentos from './PermisosDocumentos';
import { registerMovement } from '../utils/registerMovement';
import { deleteObject, ref as storageRef } from 'firebase/storage';

const ADMIN_ROLES = ['admin', 'admin_fracc', 'administrador', 'manager', 'platformadmin', 'plataforma', 'superadmin'];

const REQUEST_STATUS_META = {
    pendiente: { label: 'Pendiente', tone: 'warning' },
    revision: { label: 'Revisión', tone: 'info' },
    aprobado: { label: 'Aprobado', tone: 'success' },
    rechazado: { label: 'Rechazado', tone: 'danger' },
};

const REQUEST_TEMPLATES = [
    {
        key: 'mudanza',
        title: 'Permiso de Mudanza',
        description: '3 documentos req.',
    },
    {
        key: 'construccion',
        title: 'Permiso de Construcción',
        description: '4 documentos req.',
    },
    {
        key: 'remodelacion',
        title: 'Remodelación / Modificación',
        description: '3 documentos req.',
    },
];

const REMODEL_TRABAJO_LABELS = {
    pintura: 'Pintura',
    cambio_pisos: 'Cambio de pisos',
    ventanas_puertas: 'Ventanas / Puertas',
    impermeabilizacion: 'Impermeabilización',
    remodelacion_cocina_bano: 'Remodelación cocina / baño',
    modificacion_fachada: 'Modificación de fachada',
    otros: 'Otros',
};

const REMODEL_RESPONSABLE_LABELS = {
    propietario: 'Propietario',
    contratista: 'Contratista',
    ambos: 'Propietario y contratista',
};

const REMODEL_RUIDO_LABELS = {
    no: 'No',
    poco: 'Poco',
    moderado: 'Moderado',
    alto: 'Alto',
};

const SOLICITUD_TYPE_LABELS = {
    mudanza: 'Mudanza',
    construccion: 'Construcción',
    remodelacion: 'Remodelación',
};

const statusTitleForMovement = (statusKey) => {
    switch (statusKey) {
        case 'revision':
            return 'en Revisión';
        case 'aprobado':
            return 'Aprobada';
        case 'rechazado':
            return 'Rechazada';
        case 'pendiente':
            return 'Pendiente';
        default:
            return REQUEST_STATUS_META[statusKey]?.label || statusKey;
    }
};

const toDateOrNull = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value.toDate === 'function') {
        try {
            const converted = value.toDate();
            return Number.isNaN(converted.getTime()) ? null : converted;
        } catch (error) {
            return null;
        }
    }
    return null;
};

const formatMovementDate = (date) => (date instanceof Date ? date.toLocaleDateString('es-MX') : null);

const buildSolicitudMovementInfo = (request) => {
    const typeKey = String(request?.tipoSolicitud || '').toLowerCase();
    const loteLabel = request?.lote || request?.houseLabel || null;
    const info = {
        typeKey,
        typeLabel: SOLICITUD_TYPE_LABELS[typeKey] || 'Solicitud',
        detailParts: [],
        metadata: {},
    };

    if (typeKey === 'mudanza') {
        const programDate = toDateOrNull(request?.fechaProgramada);
        const fechaLabel = formatMovementDate(programDate);
        info.detailParts.push(`Tipo ${request?.tipoMudanza || 'programada'}`);
        if (fechaLabel) {
            info.detailParts.push(fechaLabel);
        }
        if (request?.horaProgramada) {
            info.detailParts.push(`${request.horaProgramada} hrs`);
        }
        if (loteLabel) {
            info.detailParts.push(`Lote ${loteLabel}`);
        }
        info.metadata = {
            tipoMudanza: request?.tipoMudanza || null,
            fechaProgramadaIso: programDate ? programDate.toISOString() : null,
            horaProgramada: request?.horaProgramada || null,
        };
    } else if (typeKey === 'construccion') {
        const inicio = toDateOrNull(request?.fechaInicioObra);
        const fin = toDateOrNull(request?.fechaTerminoObra);
        const inicioLabel = formatMovementDate(inicio);
        const finLabel = formatMovementDate(fin);
        info.detailParts.push(request?.tipoConstruccion || 'Construcción');
        if (inicioLabel && finLabel) {
            info.detailParts.push(`${inicioLabel} - ${finLabel}`);
        } else if (inicioLabel) {
            info.detailParts.push(inicioLabel);
        }
        if (request?.empresaConstructora) {
            info.detailParts.push(request.empresaConstructora);
        }
        if (loteLabel) {
            info.detailParts.push(`Lote ${loteLabel}`);
        }
        info.metadata = {
            tipoConstruccion: request?.tipoConstruccion || null,
            empresaConstructora: request?.empresaConstructora || null,
            fechaInicioObraIso: inicio ? inicio.toISOString() : null,
            fechaTerminoObraIso: fin ? fin.toISOString() : null,
        };
    } else if (typeKey === 'remodelacion') {
        const trabajoKey = String(request?.tipoTrabajo || '').toLowerCase();
        const trabajoLabel = REMODEL_TRABAJO_LABELS[trabajoKey] || request?.tipoTrabajo || 'Remodelación';
        const inicio = toDateOrNull(request?.fechaInicioTrabajo);
        const inicioLabel = formatMovementDate(inicio);
        const dias = Number(request?.duracionDias);
        const ruidoKey = String(request?.generaraRuido || '').toLowerCase();
        const ruidoLabel = REMODEL_RUIDO_LABELS[ruidoKey] || request?.generaraRuido || null;

        info.detailParts.push(trabajoLabel);
        if (inicioLabel) {
            info.detailParts.push(inicioLabel);
        }
        if (!Number.isNaN(dias) && dias > 0) {
            info.detailParts.push(`${dias} días`);
        }
        if (ruidoLabel) {
            info.detailParts.push(`Ruido ${ruidoLabel}`);
        }
        if (loteLabel) {
            info.detailParts.push(`Lote ${loteLabel}`);
        }
        info.metadata = {
            tipoTrabajo: request?.tipoTrabajo || null,
            generaraRuido: request?.generaraRuido || null,
            fechaInicioTrabajoIso: inicio ? inicio.toISOString() : null,
            duracionDias: !Number.isNaN(dias) && dias > 0 ? dias : null,
        };
    } else if (loteLabel) {
        info.detailParts.push(`Lote ${loteLabel}`);
    }

    info.detailText = info.detailParts.filter(Boolean).join(' · ');
    return info;
};

const sanitizeStorageFileName = (name) => {
    if (!name || typeof name !== 'string') {
        return null;
    }
    return name.replace(/[^\w.-]/g, '_');
};

const formatDateTime = (timestamp) => {
    if (!timestamp) {
        return 'Pendiente';
    }
    if (timestamp.toDate) {
        try {
            return timestamp.toDate().toLocaleString('es-MX');
        } catch (error) {
            return 'Fecha inválida';
        }
    }
    return 'Fecha inválida';
};

const formatDateOnly = (timestamp) => {
    if (!timestamp) {
        return 'Sin fecha';
    }
    if (timestamp.toDate) {
        try {
            return timestamp.toDate().toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (error) {
            return 'Fecha inválida';
        }
    }
    return 'Fecha inválida';
};

const formatTimeLabel = (timeValue) => {
    if (!timeValue || typeof timeValue !== 'string') {
        return '-';
    }
    const [hours, minutes] = timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return timeValue;
    }
    const hour12 = hours % 12 || 12;
    const period = hours >= 12 ? 'p.m.' : 'a.m.';
    const formattedHours = String(hour12).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    return `${formattedHours}:${formattedMinutes} ${period}`;
};

const formatSize = (bytes) => {
    if (!bytes || Number.isNaN(Number(bytes))) {
        return null;
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = Number(bytes);
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const sortByCreatedAtDesc = (a, b) => {
    const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return bTime - aTime;
};

const ValidacionDocumentos = () => {
    const { currentUser, userData } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [generalDocs, setGeneralDocs] = useState([]);
    const [areaDocs, setAreaDocs] = useState([]);
    const [residentDocs, setResidentDocs] = useState([]);
    const [miDocumentacion, setMiDocumentacion] = useState([]);
    const [loadingMiDocumentacion, setLoadingMiDocumentacion] = useState(false);
    const [activeTab, setActiveTab] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('tab') || 'reglamentos';
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [requestFilter, setRequestFilter] = useState('todos');
    const [showAllRequests, setShowAllRequests] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');
    const [showMudanzaModal, setShowMudanzaModal] = useState(false);
    const [showConstruccionModal, setShowConstruccionModal] = useState(false);
    const [showRemodelacionModal, setShowRemodelacionModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [updatingRequest, setUpdatingRequest] = useState(false);
    const [requestError, setRequestError] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [deletingDocumentIds, setDeletingDocumentIds] = useState([]);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const syncingEventsRef = useRef(new Set());
    const messageTimeoutRef = useRef(null);

    const normalizedRole = useMemo(() => String(userData?.rol || '').toLowerCase(), [userData]);
    const fraccionamientoId = useMemo(
        () => userData?.fraccionamientoId || userData?.fraccionamientoTd || null,
        [userData],
    );
    const isAdmin = useMemo(() => ADMIN_ROLES.includes(normalizedRole), [normalizedRole]);
    const activeDocs = useMemo(() => {
        const merged = [...generalDocs, ...areaDocs];
        return merged.sort(sortByCreatedAtDesc);
    }, [areaDocs, generalDocs]);

    const filteredDocs = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return activeDocs;
        }
        return activeDocs.filter((doc) => {
            const haystack = `${doc.titulo || doc.fileName || ''} ${doc.descripcion || ''}`.toLowerCase();
            return haystack.includes(term);
        });
    }, [activeDocs, searchTerm]);

    useEffect(() => {
        const tabParam = (searchParams.get('tab') || '').toLowerCase();
        let resolved = 'reglamentos';
        if (tabParam === 'solicitudes') {
            resolved = 'solicitudes';
        } else if (tabParam === 'mi-documentacion') {
            resolved = 'mi-documentacion';
        } else if (tabParam === 'documentos-permisos') {
            resolved = 'documentos-permisos';
        }
        setActiveTab((prev) => (prev === resolved ? prev : resolved));
    }, [searchParams]);

    const updateTab = useCallback((nextTab) => {
        console.log('🔄 [ValidacionDocumentos] Cambiando tab a:', nextTab);
        setActiveTab(nextTab);
        const nextParams = new URLSearchParams(searchParams);
        if (nextTab === 'reglamentos') {
            nextParams.delete('tab');
        } else {
            nextParams.set('tab', nextTab);
        }
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const filteredRequests = useMemo(() => {
        if (requestFilter === 'todos') {
            return residentDocs;
        }
        return residentDocs.filter((doc) => (doc.estado || 'pendiente').toLowerCase() === requestFilter);
    }, [residentDocs, requestFilter]);

    useEffect(() => {
        if (!selectedRequest) {
            return;
        }
        const latest = residentDocs.find((doc) => doc.id === selectedRequest.id);
        if (latest && latest !== selectedRequest) {
            setSelectedRequest(latest);
        }
    }, [residentDocs, selectedRequest]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setGeneralDocs([]);
            setAreaDocs([]);
            setResidentDocs([]);
            return undefined;
        }

        const documentosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
        const unsubscribeGeneral = onSnapshot(
            query(documentosRef, where('segmento', '==', 'general')),
            (snapshot) => {
                const docs = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
                setGeneralDocs(docs.sort(sortByCreatedAtDesc));
            },
            (error) => {
                console.error('No se pudieron cargar los reglamentos generales', error);
                setGeneralDocs([]);
            },
        );
        const unsubscribeAreas = onSnapshot(
            query(documentosRef, where('segmento', '==', 'areas')),
            (snapshot) => {
                const docs = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
                setAreaDocs(docs.sort(sortByCreatedAtDesc));
            },
            (error) => {
                console.error('No se pudieron cargar los reglamentos de áreas comunes', error);
                setAreaDocs([]);
            },
        );
        let unsubscribeSolicitudes = () => { };
        if (isAdmin) {
            unsubscribeSolicitudes = onSnapshot(
                query(documentosRef, where('segmento', '==', 'solicitudes')),
                (snapshot) => {
                    const docs = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
                    setResidentDocs(docs.sort(sortByCreatedAtDesc));
                },
                (error) => {
                    console.error('No se pudieron cargar las solicitudes de residentes', error);
                    setResidentDocs([]);
                },
            );
        } else if (currentUser?.uid) {
            unsubscribeSolicitudes = onSnapshot(
                query(
                    documentosRef,
                    where('segmento', '==', 'solicitudes'),
                    where('uploadedBy', '==', currentUser.uid),
                ),
                (snapshot) => {
                    const docs = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
                    setResidentDocs(docs.sort(sortByCreatedAtDesc));
                },
                (error) => {
                    console.error('No se pudieron cargar tus solicitudes enviadas', error);
                    setResidentDocs([]);
                },
            );
        }
        // Cargar "Mi Documentación" para residentes
        let unsubscribeMiDocumentacion = () => { };
        if (!isAdmin && fraccionamientoId && userData) {
            const casaId = userData?.casaId || userData?.casaID || userData?.idCasa;
            const numeroCasa = userData?.numeroCasa || userData?.casaNumero || userData?.numero;

            console.log('🔍 [ValidacionDocumentos] Buscando documentación para residente:', {
                casaId,
                numeroCasa,
                fraccionamientoId,
                userDataKeys: Object.keys(userData || {}),
                userEmail: userData?.email,
                userId: currentUser?.uid
            });

            setLoadingMiDocumentacion(true);
            // Buscar la casa del residente
            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const findCasa = async () => {
                try {
                    let casaDocId = casaId;

                    // Si no tenemos casaId directo, buscar por número
                    if (!casaDocId && numeroCasa) {
                        console.log('🔍 [ValidacionDocumentos] Buscando casa por número:', numeroCasa);
                        // Intentar con String primero
                        const q = query(casasRef, where('numero', '==', String(numeroCasa)));
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            casaDocId = snap.docs[0].id;
                            console.log('✅ [ValidacionDocumentos] Casa encontrada por número (String):', {
                                numeroCasa,
                                casaDocId,
                                casaData: snap.docs[0].data()
                            });
                        } else {
                            // Intentar sin conversión
                            console.log('🔍 [ValidacionDocumentos] Intentando búsqueda sin conversión...');
                            const q2 = query(casasRef, where('numero', '==', numeroCasa));
                            const snap2 = await getDocs(q2);
                            if (!snap2.empty) {
                                casaDocId = snap2.docs[0].id;
                                console.log('✅ [ValidacionDocumentos] Casa encontrada (sin conversión):', casaDocId);
                            } else {
                                console.warn('⚠️ [ValidacionDocumentos] No se encontró casa con número:', numeroCasa);
                            }
                        }
                    }

                    // Fallback: buscar por userId si no encontramos por casaId o numeroCasa
                    if (!casaDocId && currentUser?.uid) {
                        console.log('🔍 [ValidacionDocumentos] Buscando casa por userId:', currentUser.uid);
                        const qByUser = query(casasRef, where('userId', '==', currentUser.uid));
                        const snapByUser = await getDocs(qByUser);
                        if (!snapByUser.empty) {
                            casaDocId = snapByUser.docs[0].id;
                            console.log('✅ [ValidacionDocumentos] Casa encontrada por userId:', {
                                userId: currentUser.uid,
                                casaDocId,
                                casaData: snapByUser.docs[0].data()
                            });
                        }
                    }

                    // Fallback: buscar por email si aún no encontramos
                    if (!casaDocId && userData?.email) {
                        console.log('🔍 [ValidacionDocumentos] Buscando casa por email:', userData.email);
                        const qByEmail = query(casasRef, where('email', '==', String(userData.email).toLowerCase()));
                        const snapByEmail = await getDocs(qByEmail);
                        if (!snapByEmail.empty) {
                            casaDocId = snapByEmail.docs[0].id;
                            console.log('✅ [ValidacionDocumentos] Casa encontrada por email:', {
                                email: userData.email,
                                casaDocId,
                                casaData: snapByEmail.docs[0].data()
                            });
                        }
                    }

                    if (casaDocId) {
                        console.log('📄 [ValidacionDocumentos] Configurando listener para documentación de casa:', casaDocId);
                        const documentacionRef = collection(
                            db,
                            'fraccionamientos',
                            fraccionamientoId,
                            'casas',
                            casaDocId,
                            'documentacion'
                        );

                        // No necesitamos where porque ya estamos en la subcolección de la casa específica
                        unsubscribeMiDocumentacion = onSnapshot(
                            query(documentacionRef),
                            (snapshot) => {
                                console.log('📄 [ValidacionDocumentos] Documentos recibidos:', snapshot.docs.length);
                                snapshot.docs.forEach((doc) => {
                                    console.log('📄 [ValidacionDocumentos] Documento:', {
                                        id: doc.id,
                                        nombre: doc.data().nombre,
                                        casaId: doc.data().casaId,
                                        fechaCreacion: doc.data().fechaCreacion
                                    });
                                });
                                const docs = snapshot.docs.map((docSnapshot) => ({
                                    id: docSnapshot.id,
                                    ...docSnapshot.data()
                                })).sort((a, b) => {
                                    const aTime = a.fechaCreacion?.toMillis?.() || 0;
                                    const bTime = b.fechaCreacion?.toMillis?.() || 0;
                                    return bTime - aTime;
                                });
                                console.log('📄 [ValidacionDocumentos] Documentos procesados y ordenados:', docs.length);
                                setMiDocumentacion(docs);
                                setLoadingMiDocumentacion(false);
                            },
                            (error) => {
                                console.error('❌ [ValidacionDocumentos] Error cargando mi documentación:', error);
                                console.error('❌ [ValidacionDocumentos] Detalles del error:', {
                                    code: error.code,
                                    message: error.message,
                                    casaDocId,
                                    fraccionamientoId,
                                    path: `fraccionamientos/${fraccionamientoId}/casas/${casaDocId}/documentacion`
                                });
                                setMiDocumentacion([]);
                                setLoadingMiDocumentacion(false);
                            }
                        );
                    } else {
                        console.warn('⚠️ [ValidacionDocumentos] No se pudo determinar casaDocId para cargar documentación después de todos los intentos');
                        setLoadingMiDocumentacion(false);
                    }
                } catch (error) {
                    console.error('❌ [ValidacionDocumentos] Error buscando casa del residente:', error);
                    setLoadingMiDocumentacion(false);
                }
            };
            findCasa();
        }

        return () => {
            unsubscribeGeneral();
            unsubscribeAreas();
            unsubscribeSolicitudes();
            unsubscribeMiDocumentacion();
        };
    }, [currentUser?.uid, fraccionamientoId, isAdmin, userData]);

    useEffect(() => () => {
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
    }, []);

    const documentTypeLabel = useCallback((doc) => {
        if (doc.segmento === 'areas') {
            return 'Áreas comunes';
        }
        return 'Reglamento general';
    }, []);

    const handleOpenUploadModal = useCallback(() => {
        if (!isAdmin) {
            return;
        }
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
        setInfoMessage('');
        setShowUploadModal(true);
    }, [isAdmin]);

    const handleTemplateClick = useCallback((template) => {
        if (template.key === 'mudanza') {
            updateTab('solicitudes');
            setInfoMessage('');
            setShowMudanzaModal(true);
            return;
        }
        if (template.key === 'construccion') {
            updateTab('solicitudes');
            setInfoMessage('');
            setShowConstruccionModal(true);
            return;
        }
        if (template.key === 'remodelacion') {
            updateTab('solicitudes');
            setInfoMessage('');
            setShowRemodelacionModal(true);
            return;
        }
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
        setInfoMessage(`La solicitud "${template.title}" se habilitará en la siguiente fase.`);
        messageTimeoutRef.current = setTimeout(() => setInfoMessage(''), 3500);
    }, [updateTab]);

    const handleReviewRequest = useCallback((request) => {
        setSelectedRequest(request);
        setRequestError('');
    }, []);

    const handleCloseReview = useCallback(() => {
        setSelectedRequest(null);
        setRequestError('');
    }, []);

    const upsertSolicitudEvent = useCallback(async (request, existingEventId = null) => {
        if (!fraccionamientoId || !request?.id) {
            return existingEventId || null;
        }

        const eventosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'eventosCitas');
        const residentName = request?.nombreCompleto || request?.uploaderNombre || 'Residente';
        const loteLabel = request?.lote || request?.houseLabel || 'sin lote definido';
        const solicitudType = (request?.tipoSolicitud || '').toLowerCase();
        const isMudanza = solicitudType === 'mudanza';
        const isRemodelacion = solicitudType === 'remodelacion';
        const isConstruccion = solicitudType === 'construccion';

        const tipoMudanzaLabel = request?.tipoMudanza || 'programada';
        const trabajoLabel = REMODEL_TRABAJO_LABELS[(request?.tipoTrabajo || '').toLowerCase()] || 'Trabajo de remodelación';
        const tipoConstruccionLabel = request?.tipoConstruccion || 'Construcción autorizada';

        const eventTitle = (() => {
            if (request?.titulo && request.titulo.trim().length > 0) {
                return request.titulo.trim();
            }
            if (isRemodelacion) {
                return `Remodelación autorizada - ${residentName}`;
            }
            if (isConstruccion) {
                return `Construcción autorizada - ${residentName}`;
            }
            return `Mudanza autorizada - ${residentName}`;
        })();

        const eventDescription = (() => {
            if (isRemodelacion) {
                return `${trabajoLabel} · ${loteLabel}`;
            }
            if (isConstruccion) {
                return `${tipoConstruccionLabel} · ${loteLabel}`;
            }
            return `Mudanza ${tipoMudanzaLabel} · ${loteLabel}`;
        })();

        const eventFecha = (() => {
            if (isRemodelacion && request?.fechaInicioTrabajo) {
                return request.fechaInicioTrabajo;
            }
            if (isConstruccion && request?.fechaInicioObra) {
                return request.fechaInicioObra;
            }
            if (isMudanza && request?.fechaProgramada) {
                return request.fechaProgramada;
            }
            return serverTimestamp();
        })();

        const eventHora = isMudanza ? (request?.horaProgramada || null) : null;
        const eventNotas = (() => {
            if (isRemodelacion) {
                return request?.descripcionTrabajos || request?.observaciones || null;
            }
            if (isConstruccion) {
                return request?.descripcionConstruccion || request?.observaciones || null;
            }
            return request?.observaciones || null;
        })();

        const eventPayload = {
            titulo: eventTitle,
            tipo: isRemodelacion ? 'remodelacion' : (isConstruccion ? 'construccion' : 'mudanza'),
            categoria: isRemodelacion ? 'remodelacion' : (isConstruccion ? 'construccion' : 'mudanza'),
            descripcion: eventDescription,
            fecha: eventFecha,
            hora: eventHora,
            ubicacion: loteLabel !== 'sin lote definido' ? loteLabel : null,
            notas: eventNotas,
            fraccionamientoId,
            referenciaSolicitudId: request.id,
            residenteNombre: residentName,
            soloAdministradores: true,
            actualizadoEn: serverTimestamp(),
            tipoSolicitud: solicitudType || null,
        };

        if (isMudanza) {
            eventPayload.tipoMudanza = request?.tipoMudanza || null;
        }
        if (isRemodelacion) {
            eventPayload.tipoTrabajo = request?.tipoTrabajo || null;
            eventPayload.responsableTrabajo = request?.responsableTrabajo || null;
            eventPayload.duracionDias = request?.duracionDias != null ? Number(request.duracionDias) : null;
            eventPayload.generaraRuido = request?.generaraRuido || null;
        }
        if (isConstruccion) {
            eventPayload.tipoConstruccion = request?.tipoConstruccion || null;
            eventPayload.fechaFin = request?.fechaTerminoObra || null;
            eventPayload.empresaConstructora = request?.empresaConstructora || null;
            eventPayload.encargadoObra = request?.encargadoObra || null;
        }

        if (existingEventId) {
            const eventRef = doc(db, 'fraccionamientos', fraccionamientoId, 'eventosCitas', existingEventId);
            await updateDoc(eventRef, eventPayload);
            try {
                const duplicatesSnapshot = await getDocs(
                    query(eventosRef, where('referenciaSolicitudId', '==', request.id)),
                );
                const deletions = [];
                duplicatesSnapshot.forEach((eventDoc) => {
                    if (eventDoc.id !== existingEventId) {
                        deletions.push(deleteDoc(eventDoc.ref));
                    }
                });
                if (deletions.length > 0) {
                    await Promise.all(deletions);
                }
            } catch (error) {
                console.error('ValidacionDocumentos: no se pudieron limpiar eventos duplicados de mudanza', error);
            }
            return existingEventId;
        }

        try {
            const existingSnapshot = await getDocs(
                query(eventosRef, where('referenciaSolicitudId', '==', request.id)),
            );
            if (!existingSnapshot.empty) {
                const [primaryDoc, ...extraDocs] = existingSnapshot.docs;
                const primaryId = primaryDoc.id;
                const primaryRef = doc(db, 'fraccionamientos', fraccionamientoId, 'eventosCitas', primaryId);
                await updateDoc(primaryRef, eventPayload);
                if (extraDocs.length > 0) {
                    await Promise.all(extraDocs.map((docSnapshot) => deleteDoc(docSnapshot.ref)));
                }
                return primaryId;
            }
        } catch (error) {
            console.error('ValidacionDocumentos: no se pudo revisar eventos existentes de mudanza', error);
        }

        const newEventRef = await addDoc(eventosRef, {
            ...eventPayload,
            creadoPorUid: userData?.uid || currentUser?.uid || null,
            creadoPorNombre: userData?.nombreCompleto || userData?.nombre || null,
            creadoEn: serverTimestamp(),
        });
        return newEventRef.id;
    }, [currentUser?.uid, fraccionamientoId, userData?.nombre, userData?.nombreCompleto, userData?.uid]);

    const handleStatusChange = useCallback(async (request, nextStatus) => {
        if (!fraccionamientoId || !request?.id) {
            return;
        }

        const previousStatus = String(request?.estado || 'pendiente').toLowerCase();
        if (previousStatus === nextStatus) {
            return;
        }

        const movementInfo = buildSolicitudMovementInfo(request);
        const statusLabelPrev = REQUEST_STATUS_META[previousStatus]?.label || previousStatus;
        const statusLabelNext = REQUEST_STATUS_META[nextStatus]?.label || nextStatus;
        const statusTitle = statusTitleForMovement(nextStatus);
        const residentName = request?.nombreCompleto || request?.uploaderNombre || 'Residente';

        setUpdatingRequest(true);
        setRequestError('');
        try {
            let linkedEventId = request?.eventoCalendarioId || null;

            if (nextStatus === 'aprobado') {
                linkedEventId = await upsertSolicitudEvent(request, linkedEventId);
            } else if (linkedEventId && previousStatus === 'aprobado') {
                const eventRef = doc(db, 'fraccionamientos', fraccionamientoId, 'eventosCitas', linkedEventId);
                try {
                    await deleteDoc(eventRef);
                } catch (eventError) {
                    console.error('ValidacionDocumentos: no se pudo eliminar el evento vinculado', eventError);
                }
                linkedEventId = null;
            }

            const requestRef = doc(db, 'fraccionamientos', fraccionamientoId, 'documentos', request.id);
            const payload = {
                estado: nextStatus,
                ultimaActualizacion: serverTimestamp(),
                revisadoEn: serverTimestamp(),
                eventoCalendarioId: linkedEventId || null,
            };
            if (typeof currentUser?.uid === 'string' && currentUser.uid.length > 0) {
                payload.revisadoPor = currentUser.uid;
            }
            await updateDoc(requestRef, payload);
            setSelectedRequest((prev) => (prev && prev.id === request.id
                ? { ...prev, estado: nextStatus, eventoCalendarioId: linkedEventId || null }
                : prev));

            const descripcionParts = [];
            if (movementInfo.detailText) {
                descripcionParts.push(movementInfo.detailText);
            }
            descripcionParts.push(`Estado: ${statusLabelPrev} → ${statusLabelNext}`);
            const descripcion = descripcionParts.filter(Boolean).join(' · ');

            const metadata = {
                solicitudId: request.id,
                tipoSolicitud: movementInfo.typeKey,
                estadoAnterior: previousStatus,
                estadoNuevo: nextStatus,
                lote: request?.lote || request?.houseLabel || null,
                eventoCalendarioId: linkedEventId || null,
                solicitanteUid: request?.uploadedBy || null,
                revisadoPor: currentUser?.uid || null,
                ...movementInfo.metadata,
            };

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Solicitud de ${movementInfo.typeLabel} ${statusTitle} - ${residentName}`,
                    descripcion,
                    categoria: 'Solicitudes y Permisos',
                    tipoMovimiento: 'solicitud_actualizada',
                    origen: 'solicitudes',
                    metadata,
                });
            } catch (logError) {
                console.error('ValidacionDocumentos: no se pudo registrar el movimiento de estado', logError);
            }
        } catch (error) {
            console.error('ValidacionDocumentos: no se pudo actualizar la solicitud', error);
            setRequestError('No se pudo actualizar el estado. Verifica tu conexión o permisos.');
        } finally {
            setUpdatingRequest(false);
        }
    }, [currentUser?.uid, fraccionamientoId, upsertSolicitudEvent]);

    useEffect(() => {
        if (!isAdmin || !fraccionamientoId) {
            return;
        }

        const approvedWithoutEvent = residentDocs.filter((doc) => (
            String(doc.estado || '').toLowerCase() === 'aprobado' && !doc.eventoCalendarioId
        ));

        if (approvedWithoutEvent.length === 0) {
            return;
        }

        approvedWithoutEvent.forEach((request) => {
            if (syncingEventsRef.current.has(request.id)) {
                return;
            }

            syncingEventsRef.current.add(request.id);
            upsertSolicitudEvent(request)
                .then(async (eventId) => {
                    if (!eventId) {
                        return;
                    }
                    const requestRef = doc(db, 'fraccionamientos', fraccionamientoId, 'documentos', request.id);
                    await updateDoc(requestRef, {
                        eventoCalendarioId: eventId,
                    });
                })
                .catch((error) => {
                    console.error('ValidacionDocumentos: no se pudo sincronizar mudanza aprobada con el calendario', error);
                })
                .finally(() => {
                    syncingEventsRef.current.delete(request.id);
                });
        });
    }, [fraccionamientoId, isAdmin, residentDocs, upsertSolicitudEvent]);

    const handleSolicitudSuccess = useCallback((message) => {
        setShowMudanzaModal(false);
        setShowConstruccionModal(false);
        setShowRemodelacionModal(false);
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
        setInfoMessage(message || 'Tu solicitud se envió correctamente.');
        messageTimeoutRef.current = setTimeout(() => setInfoMessage(''), 4500);
    }, []);

    const handleUploadSuccess = useCallback((message) => {
        setShowUploadModal(false);
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
        setInfoMessage(message || 'Documento cargado correctamente.');
        messageTimeoutRef.current = setTimeout(() => setInfoMessage(''), 4500);
    }, []);

    const deleteDocument = useCallback(async (documento) => {
        if (!isAdmin || !fraccionamientoId || !documento?.id) {
            return;
        }

        setDeletingDocumentIds((prev) => (prev.includes(documento.id) ? prev : [...prev, documento.id]));
        if (messageTimeoutRef.current) {
            clearTimeout(messageTimeoutRef.current);
        }
        setInfoMessage('');

        try {
            const storageFileName = sanitizeStorageFileName(documento.fileName);
            if (storageFileName) {
                const reglamentoPath = `fraccionamientos/${fraccionamientoId}/reglamentos/${documento.id}/${storageFileName}`;
                try {
                    const fileRef = storageRef(storage, reglamentoPath);
                    await deleteObject(fileRef);
                } catch (storageError) {
                    console.warn('ValidacionDocumentos: no se pudo eliminar el archivo del reglamento', storageError);
                }
            }

            const documentoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'documentos', documento.id);
            await deleteDoc(documentoRef);

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Reglamento eliminado - ${documento.titulo || documento.fileName || 'Documento'}`,
                    descripcion: `Segmento: ${(documento.segmento || 'general').toUpperCase()}${documento.fileName ? ` · Archivo: ${documento.fileName}` : ''}`,
                    categoria: 'Reglamentos',
                    tipoMovimiento: 'documento_eliminado',
                    origen: 'reglamentos',
                    metadata: {
                        documentoId: documento.id,
                        segmento: documento.segmento || null,
                        fileName: documento.fileName || null,
                        eliminadoPor: currentUser?.uid || null,
                    },
                });
            } catch (logError) {
                console.error('ValidacionDocumentos: no se pudo registrar el movimiento de eliminación', logError);
            }

            setInfoMessage('Documento eliminado correctamente.');
            messageTimeoutRef.current = setTimeout(() => setInfoMessage(''), 4500);
        } catch (deleteError) {
            console.error('ValidacionDocumentos: no se pudo eliminar el reglamento', deleteError);
            setInfoMessage('No pudimos eliminar el documento. Intenta nuevamente.');
            messageTimeoutRef.current = setTimeout(() => setInfoMessage(''), 4500);
        } finally {
            setDeletingDocumentIds((prev) => prev.filter((id) => id !== documento.id));
            setDocumentToDelete(null);
        }
    }, [currentUser?.uid, fraccionamientoId, isAdmin]);

    const handleDeleteDocument = useCallback((documento) => {
        if (!isAdmin || !documento?.id) {
            return;
        }
        setDocumentToDelete(documento);
    }, [isAdmin]);

    const renderDocumentRow = useCallback((docItem) => {
        const isDeleting = deletingDocumentIds.includes(docItem.id);
        return (
            <li key={docItem.id} className="vd-doc-item">
                <div className="vd-doc-icon">
                    <FaFileAlt />
                </div>
                <div className="vd-doc-content">
                    <h4>{docItem.titulo || docItem.fileName}</h4>
                    <div className="vd-doc-meta">
                        <span>{documentTypeLabel(docItem)}</span>
                        {docItem.fileSize && <span>{formatSize(docItem.fileSize)}</span>}
                        <span>{formatDateTime(docItem.createdAt)}</span>
                    </div>
                </div>
                <div className="vd-actions">
                    {docItem.url && (
                        <a className="vd-icon-button" href={docItem.url} target="_blank" rel="noopener noreferrer" title="Ver">
                            <FaEye />
                        </a>
                    )}
                    {docItem.url && (
                        <a className="vd-icon-button" href={docItem.url} target="_blank" rel="noopener noreferrer" download title="Descargar">
                            <FaDownload />
                        </a>
                    )}
                    {isAdmin && (
                        <button
                            type="button"
                            className={`vd-icon-button danger${isDeleting ? ' busy' : ''}`}
                            title="Eliminar"
                            onClick={() => handleDeleteDocument(docItem)}
                            disabled={isDeleting}
                            aria-busy={isDeleting}
                        >
                            <FaTrashAlt />
                        </button>
                    )}
                </div>
            </li>
        );
    }, [deletingDocumentIds, documentTypeLabel, handleDeleteDocument, isAdmin]);

    const renderSolicitudRow = useCallback((doc) => {
        const statusKey = (doc.estado || 'pendiente').toLowerCase();
        const statusInfo = REQUEST_STATUS_META[statusKey] || REQUEST_STATUS_META.pendiente;
        const tipoSolicitud = (doc.tipoSolicitud || '').toLowerCase();
        const isConstruccion = tipoSolicitud === 'construccion';
        const isRemodelacion = tipoSolicitud === 'remodelacion';

        const attachments = (() => {
            if (isConstruccion) {
                return ([
                    doc.docLicenciaUrl && {
                        key: 'licencia',
                        label: 'Licencia municipal',
                        url: doc.docLicenciaUrl,
                    },
                    doc.docPlanosUrl && {
                        key: 'planos',
                        label: 'Planos arquitectónicos',
                        url: doc.docPlanosUrl,
                    },
                    doc.docIdentificacionUrl && {
                        key: 'identificacion',
                        label: 'Identificación del propietario',
                        url: doc.docIdentificacionUrl,
                    },
                    doc.docPredialUrl && {
                        key: 'predial',
                        label: 'Recibo de predial',
                        url: doc.docPredialUrl,
                    },
                    doc.docCronogramaUrl && {
                        key: 'cronograma',
                        label: 'Cronograma de obra',
                        url: doc.docCronogramaUrl,
                    },
                ].filter(Boolean));
            }
            if (isRemodelacion) {
                return ([
                    doc.docIdentificacionUrl && {
                        key: 'identificacion',
                        label: 'Identificación oficial',
                        url: doc.docIdentificacionUrl,
                    },
                    doc.docFotografiasUrl && {
                        key: 'fotografias',
                        label: 'Fotografías del área',
                        url: doc.docFotografiasUrl,
                    },
                    doc.docBocetoUrl && {
                        key: 'boceto',
                        label: 'Boceto o diseño',
                        url: doc.docBocetoUrl,
                    },
                ].filter(Boolean));
            }
            return ([
                doc.docIdentificacionUrl && {
                    key: 'identificacion',
                    label: 'Identificación oficial',
                    url: doc.docIdentificacionUrl,
                },
                doc.docComprobanteUrl && {
                    key: 'comprobante',
                    label: 'Comprobante de domicilio',
                    url: doc.docComprobanteUrl,
                },
                doc.docInventarioUrl && {
                    key: 'inventario',
                    label: 'Inventario',
                    url: doc.docInventarioUrl,
                },
            ].filter(Boolean));
        })();

        const tipoLabel = (() => {
            if (isConstruccion) {
                return 'Construcción';
            }
            if (isRemodelacion) {
                return 'Remodelación / Modificación';
            }
            if (doc.tipoMudanza) {
                return doc.tipoMudanza.charAt(0).toUpperCase() + doc.tipoMudanza.slice(1);
            }
            return doc.tipoSolicitud ? doc.tipoSolicitud.charAt(0).toUpperCase() + doc.tipoSolicitud.slice(1) : 'Mudanza';
        })();

        const obraPeriodo = () => {
            if (!doc.fechaInicioObra && !doc.fechaTerminoObra) {
                return 'Periodo por definir';
            }
            const fechaInicio = doc.fechaInicioObra ? formatDateOnly(doc.fechaInicioObra) : 'Inicio pendiente';
            const fechaTermino = doc.fechaTerminoObra ? formatDateOnly(doc.fechaTerminoObra) : 'Termino pendiente';
            return `${fechaInicio} - ${fechaTermino}`;
        };

        const metaItems = (() => {
            if (isConstruccion) {
                return [
                    {
                        key: 'periodo',
                        icon: <FaCalendarAlt />,
                        text: obraPeriodo(),
                    },
                    {
                        key: 'tipo',
                        icon: <FaHammer />,
                        text: doc.tipoConstruccion || 'Tipo por definir',
                    },
                    {
                        key: 'lote',
                        icon: null,
                        text: doc.lote || doc.casa || doc.houseLabel || 'Lote sin asignar',
                    },
                ];
            }
            if (isRemodelacion) {
                return [
                    {
                        key: 'tipo',
                        icon: <FaHammer />,
                        text: REMODEL_TRABAJO_LABELS[(doc.tipoTrabajo || '').toLowerCase()] || 'Trabajo por definir',
                    },
                    {
                        key: 'inicio',
                        icon: <FaCalendarAlt />,
                        text: doc.fechaInicioTrabajo ? formatDateOnly(doc.fechaInicioTrabajo) : 'Inicio por definir',
                    },
                    {
                        key: 'duracion',
                        icon: <FaClock />,
                        text: doc.duracionDias ? `${doc.duracionDias} días` : 'Duración por definir',
                    },
                ];
            }
            return [
                {
                    key: 'fecha',
                    icon: <FaCalendarAlt />,
                    text: doc.fechaProgramada ? formatDateOnly(doc.fechaProgramada) : 'Fecha por definir',
                },
                {
                    key: 'hora',
                    icon: <FaClock />,
                    text: doc.horaProgramada ? `${doc.horaProgramada} hrs` : 'Horario por definir',
                },
                {
                    key: 'lote',
                    icon: null,
                    text: doc.lote || doc.casa || doc.houseLabel || 'Lote sin asignar',
                },
            ];
        })();

        const extraItems = (() => {
            if (isConstruccion) {
                return [
                    doc.empresaConstructora && `Constructora: ${doc.empresaConstructora}`,
                    doc.encargadoObra && `Encargado: ${doc.encargadoObra}`,
                    doc.horarioLlegada && `Horario: ${formatTimeLabel(doc.horarioLlegada)}`,
                ].filter(Boolean);
            }
            if (isRemodelacion) {
                return [
                    doc.responsableTrabajo && `Responsable: ${REMODEL_RESPONSABLE_LABELS[(doc.responsableTrabajo || '').toLowerCase()] || doc.responsableTrabajo}`,
                    doc.nombreContratista && `Contratista: ${doc.nombreContratista}`,
                    doc.generaraRuido && `Ruido estimado: ${REMODEL_RUIDO_LABELS[(doc.generaraRuido || '').toLowerCase()] || doc.generaraRuido}`,
                    doc.horarioLlegada && `Horario: ${formatTimeLabel(doc.horarioLlegada)}`,
                ].filter(Boolean);
            }
            return [
                doc.empresaMudanza && `Empresa: ${doc.empresaMudanza}`,
                doc.placasVehiculos && `Placas: ${doc.placasVehiculos}`,
            ].filter(Boolean);
        })();

        return (
            <li key={doc.id} className={`vd-request-item ${isAdmin ? 'admin' : 'resident'}`}>
                <div className="vd-request-avatar">
                    <FaUserCircle />
                </div>
                <div className="vd-request-body">
                    <div className="vd-request-head">
                        <h4>{doc.uploaderNombre || 'Residente'}</h4>
                        <span className={`vd-badge ${statusInfo.tone}`}>{statusInfo.label}</span>
                    </div>
                    <p>{doc.titulo || `Solicitud de ${tipoLabel}`}</p>
                    <div className="vd-request-meta">
                        {metaItems.map((item) => (
                            <span key={item.key}>
                                {item.icon}
                                {item.icon ? ' ' : ''}
                                {item.text}
                            </span>
                        ))}
                    </div>
                    {extraItems.length > 0 && (
                        <div className="vd-request-extra">
                            {extraItems.map((item) => (
                                <span key={item}>{item}</span>
                            ))}
                        </div>
                    )}
                    {attachments.length > 0 && (
                        <div className="vd-request-files">
                            {attachments.map((file) => (
                                <a
                                    key={file.key}
                                    className="vd-file-link"
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FaFileAlt />
                                    <span>{file.label}</span>
                                </a>
                            ))}
                        </div>
                    )}
                    <div className="vd-request-submitted">Registrada el {formatDateTime(doc.createdAt)}</div>
                </div>
                {isAdmin && (
                    <button type="button" className="vd-button ghost" onClick={() => handleReviewRequest(doc)}>
                        Revisar
                    </button>
                )}
            </li>
        );
    }, [handleReviewRequest, isAdmin]);

    const isDeletingPendingDocument = documentToDelete ? deletingDocumentIds.includes(documentToDelete.id) : false;

    return (
        <div className="validacion-documentos-page">
            <header className="vd-header">
                <div className="vd-header-icon">
                    <FaFolderOpen />
                </div>
                <div>
                    <h1>Solicitudes y Permisos</h1>
                    <p>Gestiona reglamentos y tramita permisos de mudanza, construcción o remodelación.</p>
                </div>
            </header>

            <div className="vd-tabs">
                <button
                    type="button"
                    className={`vd-tab ${activeTab === 'reglamentos' ? 'active' : ''}`}
                    onClick={() => updateTab('reglamentos')}
                >
                    <span>Reglamentos</span>
                </button>
                {!isAdmin && (
                    <button
                        type="button"
                        className={`vd-tab ${activeTab === 'mi-documentacion' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (activeTab !== 'mi-documentacion') {
                                updateTab('mi-documentacion');
                            }
                        }}
                    >
                        <span>Mi Documentación</span>
                    </button>
                )}
                <button
                    type="button"
                    className={`vd-tab ${activeTab === 'solicitudes' ? 'active' : ''}`}
                    onClick={() => updateTab('solicitudes')}
                >
                    <span>{isAdmin ? 'Solicitudes Recibidas' : 'Mis Solicitudes'}</span>
                </button>
                {isAdmin && (
                    <button
                        type="button"
                        className={`vd-tab ${activeTab === 'documentos-permisos' ? 'active' : ''}`}
                        onClick={() => updateTab('documentos-permisos')}
                    >
                        <span>Documentos/Permisos</span>
                    </button>
                )}
            </div>

            {infoMessage && (
                <div className="vd-alert info">{infoMessage}</div>
            )}

            {activeTab === 'reglamentos' && (
                <section className="vd-card">
                    <div className="vd-toolbar">
                        <div className="vd-search">
                            <input
                                type="text"
                                placeholder="Buscar documento..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                            <button type="button" className="vd-button ghost" onClick={() => setSearchTerm('')}>
                                <FaFilter />
                                Filtrar
                            </button>
                        </div>
                        {isAdmin && (
                            <button type="button" className="vd-button" onClick={handleOpenUploadModal}>
                                <FaCloudUploadAlt />
                                Subir Documento
                            </button>
                        )}
                    </div>

                    {filteredDocs.length === 0 ? (
                        <p className="vd-empty">No hay documentos publicados todavía.</p>
                    ) : (
                        <ul className="vd-doc-list">
                            {filteredDocs.map(renderDocumentRow)}
                        </ul>
                    )}
                </section>
            )}

            {activeTab === 'mi-documentacion' && !isAdmin && (
                <section className="vd-card">
                    <h3>Mi Documentación</h3>
                    <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                        Documentos cargados por la administración para tu casa.
                    </p>
                    {loadingMiDocumentacion ? (
                        <p className="vd-empty">Cargando documentos...</p>
                    ) : miDocumentacion.length === 0 ? (
                        <p className="vd-empty">No hay documentos cargados aún.</p>
                    ) : (
                        <ul className="vd-doc-list">
                            {miDocumentacion.map((doc) => (
                                <li key={doc.id} className="vd-doc-item">
                                    <div className="vd-doc-icon">
                                        <FaFileAlt style={{ color: doc.tipo?.includes('pdf') ? '#dc2626' : '#3b82f6' }} />
                                    </div>
                                    <div className="vd-doc-info">
                                        <strong>{doc.nombre}</strong>
                                        <span>
                                            {doc.tamaño ? `${(doc.tamaño / 1024).toFixed(1)} KB` : ''}
                                            {doc.fechaCreacion ? ` • ${doc.fechaCreacion.toDate().toLocaleDateString('es-MX')}` : ''}
                                        </span>
                                    </div>
                                    <div className="vd-doc-actions">
                                        <button
                                            type="button"
                                            className="vd-action-btn"
                                            onClick={() => window.open(doc.url, '_blank')}
                                            title="Ver documento"
                                        >
                                            <FaEye />
                                        </button>
                                        <button
                                            type="button"
                                            className="vd-action-btn"
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(doc.url);
                                                    const blob = await response.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = doc.nombre;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    window.URL.revokeObjectURL(url);
                                                    document.body.removeChild(a);
                                                } catch (error) {
                                                    console.error('Error descargando:', error);
                                                    alert('Error al descargar el documento');
                                                }
                                            }}
                                            title="Descargar documento"
                                        >
                                            <FaDownload />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {activeTab === 'solicitudes' && isAdmin && (
                <section className="vd-card">
                    <div className="vd-request-toolbar">
                        <div className="vd-request-filters">
                            {['todos', 'pendiente', 'revision', 'aprobado', 'rechazado'].map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`vd-chip ${requestFilter === key ? 'selected' : ''}`}
                                    onClick={() => setRequestFilter(key)}
                                >
                                    {key === 'todos' ? 'Todos' : REQUEST_STATUS_META[key].label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {filteredRequests.length === 0 ? (
                        <p className="vd-empty">Aún no recibes solicitudes de los colonos.</p>
                    ) : (
                        <ul className="vd-request-list">
                            {filteredRequests.map(renderSolicitudRow)}
                        </ul>
                    )}
                </section>
            )}

            {activeTab === 'solicitudes' && !isAdmin && (
                <>
                    <section className="vd-card">
                        <h3>Nueva Solicitud</h3>
                        <div className="vd-new-request-grid">
                            {REQUEST_TEMPLATES.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className="vd-request-template"
                                    onClick={() => handleTemplateClick(item)}
                                >
                                    <div className="vd-template-icon">
                                        <FaPlus />
                                    </div>
                                    <div>
                                        <strong>{item.title}</strong>
                                        <span>{item.description}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>
                    <section className="vd-card">
                        <h3>Mis Solicitudes</h3>
                        {residentDocs.length === 0 ? (
                            <p className="vd-empty">Aún no has enviado documentación a la administración.</p>
                        ) : (
                            <>
                                <ul className="vd-request-list">
                                    {(showAllRequests ? residentDocs : residentDocs.slice(0, 1)).map(renderSolicitudRow)}
                                </ul>
                                {residentDocs.length > 1 && (
                                    <button
                                        className="vd-show-more-btn"
                                        onClick={() => setShowAllRequests(!showAllRequests)}
                                    >
                                        {showAllRequests ? 'Ver menos' : `Ver todas (${residentDocs.length})`}
                                    </button>
                                )}
                            </>
                        )}
                    </section>
                </>
            )}

            {activeTab === 'documentos-permisos' && isAdmin && (
                <PermisosDocumentos />
            )}

            {showMudanzaModal && fraccionamientoId && (
                <SolicitudMudanzaModal
                    fraccionamientoId={fraccionamientoId}
                    currentUser={currentUser}
                    userData={userData}
                    onClose={() => setShowMudanzaModal(false)}
                    onSuccess={handleSolicitudSuccess}
                />
            )}

            {showConstruccionModal && fraccionamientoId && (
                <SolicitudConstruccionModal
                    fraccionamientoId={fraccionamientoId}
                    currentUser={currentUser}
                    userData={userData}
                    onClose={() => setShowConstruccionModal(false)}
                    onSuccess={handleSolicitudSuccess}
                />
            )}

            {showRemodelacionModal && fraccionamientoId && (
                <SolicitudRemodelacionModal
                    fraccionamientoId={fraccionamientoId}
                    currentUser={currentUser}
                    userData={userData}
                    onClose={() => setShowRemodelacionModal(false)}
                    onSuccess={handleSolicitudSuccess}
                />
            )}
            {showUploadModal && fraccionamientoId && (
                <ReglamentoUploadModal
                    fraccionamientoId={fraccionamientoId}
                    currentUser={currentUser}
                    userData={userData}
                    onClose={() => setShowUploadModal(false)}
                    onSuccess={handleUploadSuccess}
                />
            )}

            {selectedRequest && (
                <SolicitudMudanzaReviewModal
                    request={selectedRequest}
                    onClose={handleCloseReview}
                    onStatusChange={(nextStatus) => handleStatusChange(selectedRequest, nextStatus)}
                    updating={updatingRequest}
                    errorMessage={requestError}
                />
            )}
            <ConfirmDialog
                open={Boolean(documentToDelete)}
                title="Eliminar documento"
                message={documentToDelete ? `¿Eliminar "${documentToDelete.titulo || documentToDelete.fileName || 'Documento'}" de forma permanente?` : ''}
                cancelLabel="Conservar"
                confirmLabel="Eliminar"
                tone="danger"
                confirmDisabled={isDeletingPendingDocument}
                onCancel={() => setDocumentToDelete(null)}
                onConfirm={async () => {
                    if (documentToDelete) {
                        await deleteDocument(documentToDelete);
                    }
                }}
            />
        </div>
    );
};

export default ValidacionDocumentos;
